import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import type {
  CreateAlternativeInput,
  DuplicateAlternativeInput,
  FrontierAlternativeList,
  FrontierF1BAdapter,
  RenameAlternativeInput,
  SetAlternativeSelectionInput,
} from "./types";

const F1B_KEY = "frontier-f1b";

export function useFrontierAlternatives(
  projectId: string,
  adapter: FrontierF1BAdapter,
  /**
   * ``"always"``: read the list again on every mount, for a screen that must
   * show a design's current state after another screen changed it (the
   * planner after the Review validates, QA-06). Other screens keep the
   * cached list.
   */
  options: { refetchOnMount?: "always" } = {},
) {
  return useQuery({
    queryKey: [F1B_KEY, "alternatives", projectId],
    queryFn: () => adapter.listAlternatives(projectId),
    enabled: Boolean(projectId),
    retry: false,
    ...(options.refetchOnMount
      ? { refetchOnMount: options.refetchOnMount }
      : {}),
  });
}

/** The catalogue changes rarely: keep it warm for the whole planning session. */
export const CATALOG_STALE_TIME = 30 * 60 * 1000;
export const CATALOG_GC_TIME = 60 * 60 * 1000;

/** Query options shared by `useFrontierCatalog` and the planner's prefetch. */
export function catalogQueryOptions(
  projectId: string,
  adapter: FrontierF1BAdapter,
) {
  return {
    queryKey: [F1B_KEY, "catalog", projectId] as const,
    queryFn: () => adapter.browseCatalog(undefined, projectId),
    staleTime: CATALOG_STALE_TIME,
    gcTime: CATALOG_GC_TIME,
    retry: false,
  };
}

/** Re-read only the designs list; the catalogue and updates stay cached. */
export function invalidateFrontierAlternatives(
  queryClient: QueryClient,
  projectId: string,
) {
  return queryClient.invalidateQueries({
    queryKey: [F1B_KEY, "alternatives", projectId],
  });
}

export function useFrontierProductUpdates(
  projectId: string,
  adapter: FrontierF1BAdapter,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [F1B_KEY, "product-updates", projectId],
    queryFn: () => adapter.listProductUpdates!(projectId),
    enabled:
      enabled && Boolean(projectId) && Boolean(adapter.listProductUpdates),
    retry: false,
    staleTime: CATALOG_STALE_TIME,
    gcTime: CATALOG_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useFrontierCatalog(
  projectId: string,
  adapter: FrontierF1BAdapter,
  enabled: boolean,
) {
  return useQuery({
    ...catalogQueryOptions(projectId, adapter),
    enabled: enabled && Boolean(projectId),
    placeholderData: keepPreviousData,
  });
}

export function useRenameFrontierAlternative(adapter: FrontierF1BAdapter) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RenameAlternativeInput) =>
      adapter.renameAlternative(input),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: [F1B_KEY, "alternatives", variables.projectId],
      }),
  });
}

export function useDuplicateFrontierAlternative(adapter: FrontierF1BAdapter) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DuplicateAlternativeInput) =>
      adapter.duplicateAlternative(input),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: [F1B_KEY, "alternatives", variables.projectId],
      }),
  });
}

export function useCreateFrontierAlternative(adapter: FrontierF1BAdapter) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAlternativeInput) =>
      adapter.createAlternative(input),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: [F1B_KEY, "alternatives", variables.projectId],
      }),
  });
}

export function useSetFrontierSelection(adapter: FrontierF1BAdapter) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetAlternativeSelectionInput) =>
      adapter.setSelection(input),
    onSuccess: (saved, variables) => {
      // Show the saved design at once; the invalidation below confirms it.
      queryClient.setQueryData<FrontierAlternativeList>(
        [F1B_KEY, "alternatives", variables.projectId],
        (current) =>
          current
            ? {
                ...current,
                alternatives: current.alternatives.map((alternative) =>
                  alternative.id === saved.id ? saved : alternative,
                ),
              }
            : current,
      );
      void invalidateFrontierAlternatives(queryClient, variables.projectId);
      // Planning reads the same governed selection through the F1C workspace.
      // Keep Catalog -> Devices immediate instead of waiting for a reload.
      void queryClient.invalidateQueries({
        queryKey: ["frontier-f1c-workspace"],
      });
    },
  });
}
